/**
 * Phase VV.63 — Auth token persistence + bearer header wiring.
 *
 * Why this exists:
 *   The Capacitor Android WebView drops the `SameSite=None; Secure`
 *   session cookie on every cold-start, so sideloaded APK users have to
 *   re-enter their login every launch. Ordinary browsers keep the
 *   cookie fine — but we can't rely on it inside a native WebView.
 *
 * Fix:
 *   1. On sign-in, the backend now echoes the `session_token` in the
 *      JSON body. This module captures that token and mirrors it into
 *      localStorage under `pys.auth.token`.
 *   2. On every subsequent `fetch()` to our REACT_APP_BACKEND_URL, we
 *      inject an `Authorization: Bearer <token>` header. Backend
 *      already accepts either the cookie OR the bearer header
 *      (see `_extract_token` in `deps.py`), so browser users are
 *      unaffected.
 *   3. On sign-out, we clear the stored token.
 *
 * Idempotent: import once from App.js at boot. Safe to call multiple
 * times; the fetch monkey-patch is guarded so it only wraps native
 * fetch once.
 */
const TOKEN_KEY = "pys.auth.token";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function saveAuthToken(token) {
  if (!token) return;
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (_) {
    /* private mode — cookie fallback will still work in most browsers */
  }
}

export function clearAuthToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (_) {
    /* nothing to do */
  }
}

export function getAuthToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (_) {
    return null;
  }
}

let _installed = false;

export function installBearerFetch() {
  if (_installed || typeof window === "undefined") return;
  if (!BACKEND_URL) return;
  const nativeFetch = window.fetch.bind(window);
  _installed = true;
  window.fetch = async (input, init = {}) => {
    // Only mess with same-backend requests to avoid leaking the token
    // to CDNs or third-party APIs the app happens to call.
    let urlStr = "";
    try {
      urlStr = typeof input === "string" ? input : (input && input.url) || "";
    } catch (_) {
      urlStr = "";
    }
    const isOurs =
      urlStr.startsWith(BACKEND_URL) ||
      urlStr.startsWith("/api/");
    if (!isOurs) return nativeFetch(input, init);

    const token = getAuthToken();
    if (!token) return nativeFetch(input, init);

    // Preserve existing Authorization header if the caller set one on
    // purpose (e.g. push VAPID flows).
    const headers = new Headers(init.headers || {});
    if (!headers.has("Authorization") && !headers.has("authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return nativeFetch(input, { ...init, headers });
  };
}
